/**
 * `<pg-chart>` — gráfico reutilizável, SVG nativo, sem dependência
 * externa (mesmo princípio de `pg-kanban`: HTML5 nativo, sem lib de
 * terceiros — `ARCHITECTURE_GUIDE.md`, seção 8). Sprint 14
 * (`BACKLOG.md`, Módulo 14 — "Gráfico de fluxo de caixa", "Gráfico de
 * origem de vendas por parceiro", "Funil de produção", "Heatmap de
 * calendário de envios").
 *
 * Dados via PROPRIEDADES JS (mesmo padrão de `pg-table`/`pg-kanban`).
 * Diferente de `pg-kpi-card` (que só exibe texto já formatado), este
 * componente PRECISA do valor numérico bruto para calcular a altura
 * das barras/intensidade do heatmap — mas continua sem saber o que o
 * valor representa (receita? contagem de OPS?). `formatarValor` é
 * opcional, usado só para o texto exibido em rótulos/tooltip; sem ele,
 * o número bruto é mostrado.
 *
 * Uso (barra, 1+ séries):
 * ```js
 * const chart = document.querySelector('pg-chart');
 * chart.type = 'bar';
 * chart.series = [{ key: 'receita', label: 'Receita', color: 'var(--pg-color-sucesso)' }];
 * chart.points = [{ label: 'Jan', values: { receita: 120000 } }];
 * chart.formatarValor = (centavos) => formatBRL(centavosParaReais(centavos));
 * ```
 *
 * Uso (heatmap de calendário):
 * ```js
 * chart.type = 'heatmap';
 * chart.cells = [{ data: '2026-08-01', quantidade: 3 }];
 * ```
 *
 * Uso (linha, 1+ séries — evolução ao longo do tempo):
 * ```js
 * chart.type = 'line';
 * chart.series = [{ key: 'receita', label: 'Receita', color: 'var(--pg-color-sucesso)' }];
 * chart.points = [{ label: 'Jan', values: { receita: 120000 } }];
 * ```
 *
 * Uso (donut — distribuição de um total em categorias, 1 valor por ponto):
 * ```js
 * chart.type = 'donut';
 * chart.points = [
 *   { label: 'Pago', values: { valor: 42 }, color: 'var(--pg-color-sucesso)' },
 *   { label: 'Pendente', values: { valor: 8 }, color: 'var(--pg-color-alerta)' },
 * ];
 * ```
 */
export class PgChart extends HTMLElement {
    root;
    _type = 'bar';
    _series = [];
    _points = [];
    _cells = [];
    _dias = null;
    _formatarValor = (valor) => String(valor);
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    get type() {
        return this._type;
    }
    set type(valor) {
        this._type = valor;
        this.render();
    }
    get series() {
        return this._series;
    }
    set series(valor) {
        this._series = valor;
        this.render();
    }
    get points() {
        return this._points;
    }
    set points(valor) {
        this._points = valor;
        this.render();
    }
    get cells() {
        return this._cells;
    }
    set cells(valor) {
        this._cells = valor;
        this.render();
    }
    /** Nº de dias que a consulta de origem cobre (opcional, só heatmap) — usado para desenhar o calendário completo (com dias reais sem envio) em vez de só o intervalo entre a 1ª e a última célula retornada. */
    get dias() {
        return this._dias;
    }
    set dias(valor) {
        this._dias = valor;
        this.render();
    }
    set formatarValor(fn) {
        this._formatarValor = fn;
        this.render();
    }
    connectedCallback() {
        this.render();
    }
    render() {
        const corpo = this._type === 'heatmap'
            ? this.renderHeatmap()
            : this._type === 'line'
                ? this.renderLinha()
                : this._type === 'donut'
                    ? this.renderDonut()
                    : this.renderBarras();
        this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .vazio {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-desabilitado, #5c626b);
          padding: var(--pg-space-4, 16px) 0;
        }
        .pg-tooltip { position: absolute; bottom: calc(100% + 11px); left: 50%; transform: translateX(-50%) translateY(4px); z-index: 5; background: var(--pg-color-bg-elevado, #1a1d21); border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: var(--pg-radius-md, 10px); padding: 8px 12px; font-size: var(--pg-font-size-xs, 0.75rem); line-height: 1.6; white-space: nowrap; box-shadow: var(--pg-shadow-lg, 0 12px 32px rgba(0, 0, 0, 0.45)); opacity: 0; pointer-events: none; transition: opacity 0.15s ease, transform 0.15s ease; }
        .pg-tooltip strong { color: var(--pg-color-texto-primario, #f2f3f5); font-weight: var(--pg-font-weight-semibold, 600); }
        .pg-tooltip-linha { display: flex; align-items: center; gap: 6px; color: var(--pg-color-texto-secundario, #9aa1ab); }
        .pg-tooltip-cor { width: 7px; height: 7px; border-radius: var(--pg-radius-full, 9999px); flex-shrink: 0; }
        .pg-tooltip-valor { color: var(--pg-color-texto-primario, #f2f3f5); font-weight: var(--pg-font-weight-semibold, 600); font-variant-numeric: tabular-nums; margin-left: auto; padding-left: var(--pg-space-3, 12px); }
        .legenda { display: flex; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); flex-wrap: wrap; }
        .legenda-item { display: flex; align-items: center; gap: 7px; font-size: var(--pg-font-size-xs, 0.75rem); font-weight: var(--pg-font-weight-medio, 500); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .legenda-cor { width: 9px; height: 9px; border-radius: var(--pg-radius-full, 9999px); flex-shrink: 0; box-shadow: 0 0 0 3px color-mix(in srgb, currentColor 12%, transparent); }
        .barras { display: flex; align-items: flex-end; justify-content: center; gap: var(--pg-space-5, 24px); height: 180px; padding-bottom: 4px; position: relative; background-image: repeating-linear-gradient(to top, color-mix(in srgb, var(--pg-color-borda, #2a2f36) 65%, transparent) 0, color-mix(in srgb, var(--pg-color-borda, #2a2f36) 65%, transparent) 1px, transparent 1px, transparent 33.33%); }
        .grupo-barra { display: flex; flex-direction: column; align-items: center; gap: var(--pg-space-2, 8px); flex: 0 1 96px; min-width: 0; height: 100%; justify-content: flex-end; }
        .colunas { display: flex; align-items: flex-end; gap: 3px; width: 100%; justify-content: center; height: 100%; position: relative; }
        .coluna-wrap { flex: 1; max-width: 36px; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 4px; }
        .coluna-valor { font-size: 10px; font-weight: var(--pg-font-weight-semibold, 600); color: var(--pg-color-texto-secundario, #9aa1ab); font-variant-numeric: tabular-nums; white-space: nowrap; opacity: 0; transition: opacity 0.15s ease; }
        .coluna-wrap:hover .coluna-valor { opacity: 1; }
        .coluna { position: relative; width: 100%; border-radius: 7px 7px 2px 2px; min-height: 2px; transition: filter 0.15s ease, box-shadow 0.15s ease; animation: pg-chart-crescer 0.45s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        .coluna:hover { filter: brightness(1.15); box-shadow: 0 6px 16px -4px color-mix(in srgb, var(--pg-color-destaque, #2f8cff) 45%, transparent); }
        .coluna:hover .pg-tooltip { opacity: 1; transform: translateX(-50%) translateY(0); }
        .coluna--destaque { filter: brightness(1.1); box-shadow: 0 -2px 0 0 var(--pg-color-texto-primario, #f2f3f5) inset; }
        .coluna-wrap--destaque .coluna-valor { opacity: 1; color: var(--pg-color-texto-primario, #f2f3f5); font-weight: var(--pg-font-weight-bold, 700); }
        @keyframes pg-chart-crescer { from { transform: scaleY(0); transform-origin: bottom; } to { transform: scaleY(1); transform-origin: bottom; } }
        @media (prefers-reduced-motion: reduce) { .coluna { animation: none; } }
        .rotulo-x { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
        .heatmap-corpo { display: flex; gap: 6px; }
        .heatmap-dias-semana { display: flex; flex-direction: column; gap: 3px; flex-shrink: 0; }
        .heatmap-dias-semana span { width: 26px; height: 12px; font-size: 9px; line-height: 12px; color: var(--pg-color-texto-desabilitado, #5c626b); white-space: nowrap; text-align: right; }
        .heatmap-semanas { display: flex; gap: 3px; overflow-x: auto; padding-bottom: 4px; }
        .heatmap-semana { display: flex; flex-direction: column; gap: 3px; }
        .heatmap-dia { width: 12px; height: 12px; border-radius: 2px; background: var(--pg-color-bg-elevado, #1a1d21); border: 1px solid var(--pg-color-borda, #2a2f36); transition: transform 0.1s ease; }
        .heatmap-dia:hover { transform: scale(1.25); }
        .heatmap-legenda { display: flex; align-items: center; gap: 4px; margin-top: var(--pg-space-2, 8px); font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); }
        .linha-wrap { position: relative; height: 200px; }
        .linha-eixo-y { position: absolute; left: 0; top: 0; bottom: 0; width: 42px; pointer-events: none; }
        .linha-eixo-y span { position: absolute; right: 8px; transform: translateY(-50%); font-size: 10px; font-weight: var(--pg-font-weight-medio, 500); color: var(--pg-color-texto-desabilitado, #5c626b); font-variant-numeric: tabular-nums; white-space: nowrap; }
        .linha-plotagem { position: absolute; left: 42px; right: 0; top: 0; bottom: 0; }
        .linha-svg { width: 100%; height: 100%; overflow: visible; display: block; }
        .linha-grade line { stroke: var(--pg-color-borda, #2a2f36); stroke-width: 0.4; stroke-dasharray: 1.5 2; vector-effect: non-scaling-stroke; }
        .linha-zero { stroke: var(--pg-color-texto-desabilitado, #5c626b); stroke-width: 0.8; vector-effect: non-scaling-stroke; }
        .linha-area { opacity: 1; }
        .linha-caminho { stroke-linejoin: round; stroke-linecap: round; filter: drop-shadow(0 0 3px color-mix(in srgb, currentColor 55%, transparent)) drop-shadow(0 1px 1px rgba(0, 0, 0, 0.3)); animation: pg-chart-desenhar 0.7s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        @keyframes pg-chart-desenhar { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        .linha-referencia { animation: pg-chart-fade 0.35s ease-out backwards; }
        @keyframes pg-chart-fade { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .linha-caminho, .linha-referencia { animation: none; } }
        .linha-rotulos { display: flex; justify-content: space-between; gap: 2px; margin-top: var(--pg-space-3, 12px); padding-left: 42px; }
        .linha-rotulos span { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); flex: 1; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .linha-marcadores { position: absolute; inset: 0; pointer-events: none; }
        .linha-ponto-html { position: absolute; width: 3px; height: 3px; border-radius: 50%; transform: translate(-50%, -50%); box-shadow: 0 0 0 1px var(--pg-color-bg-elevado, #1a1d21); }
        .linha-ponto-html--principal { width: 3px; height: 3px; }
        .linha-colunas-hover { position: absolute; inset: 0; }
        .linha-coluna-hover { position: absolute; top: 0; bottom: 0; cursor: default; }
        .linha-guia { position: absolute; top: 0; bottom: 0; width: 1px; background: var(--pg-color-borda, #2a2f36); opacity: 0; transition: opacity 0.12s ease; }
        .linha-coluna-hover:hover .linha-guia { opacity: 1; }
        .linha-coluna-hover .pg-tooltip { bottom: auto; left: 50%; transform: translate(-50%, calc(-100% - 14px)); }
        .linha-coluna-hover:hover .pg-tooltip { opacity: 1; transform: translate(-50%, calc(-100% - 10px)); }
        .donut-wrap { display: flex; align-items: center; gap: var(--pg-space-6, 32px); flex-wrap: wrap; }
        .donut-svg { flex-shrink: 0; filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.3)); }
        .donut-fatia { transition: opacity 0.15s ease, filter 0.15s ease; animation: pg-chart-fatia 0.6s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        .donut-fatia:hover { opacity: 0.88; filter: brightness(1.12); }
        @keyframes pg-chart-fatia { from { stroke-dasharray: 0 999; } }
        @media (prefers-reduced-motion: reduce) { .donut-fatia { animation: none; } }
        .donut-centro-valor { font-family: var(--pg-font-familia-display, system-ui); font-size: 7px; font-weight: var(--pg-font-weight-bold, 700); font-variant-numeric: tabular-nums; fill: var(--pg-color-texto-primario, #f2f3f5); letter-spacing: -0.01em; }
        .donut-centro-rotulo { font-size: 2.5px; fill: var(--pg-color-texto-secundario, #9aa1ab); text-transform: uppercase; letter-spacing: 0.08em; font-weight: var(--pg-font-weight-semibold, 600); }
        .donut-legenda { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); min-width: 0; }
        .donut-legenda-item { display: flex; align-items: center; gap: var(--pg-space-2, 8px); font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); white-space: nowrap; padding: 4px 6px; margin: -4px -6px; border-radius: var(--pg-radius-sm, 6px); transition: background 0.15s ease; }
        .donut-legenda-item:hover { background: var(--pg-color-bg-hover, #22262b); }
        .donut-legenda-valores { display: flex; align-items: baseline; gap: 6px; margin-left: auto; padding-left: var(--pg-space-4, 16px); }
        .donut-legenda-valor { color: var(--pg-color-texto-primario, #f2f3f5); font-weight: var(--pg-font-weight-semibold, 600); font-variant-numeric: tabular-nums; }
        .donut-legenda-percentual { color: var(--pg-color-texto-desabilitado, #5c626b); font-size: var(--pg-font-size-xs, 0.75rem); font-variant-numeric: tabular-nums; min-width: 3ch; text-align: right; }
      </style>
      ${corpo}
    `;
    }
    renderBarras() {
        if (this._points.length === 0) {
            return `<p class="vazio">Sem dados no período.</p>`;
        }
        const series = this._series.length > 0 ? this._series : [{ key: 'valor', label: '' }];
        const maximo = Math.max(1, ...this._points.flatMap((ponto) => series.map((s) => ponto.values[s.key] ?? 0)));
        const legenda = series.length > 1
            ? `<div class="legenda">${series
                .map((s) => `<span class="legenda-item"><span class="legenda-cor" style="background:${s.color ?? 'var(--pg-color-destaque)'}"></span>${escaparHtml(s.label)}</span>`)
                .join('')}</div>`
            : '';
        // Destaca a maior barra do conjunto (só quando houver de fato variação, senão toda barra "ganharia" o destaque).
        const valores = this._points.flatMap((ponto) => series.map((s) => ponto.values[s.key] ?? 0));
        const indiceMaiorValor = valores.every((v) => v === valores[0]) ? -1 : valores.indexOf(maximo);
        const grupos = this._points
            .map((ponto, indiceGrupo) => {
            const colunas = series
                .map((s, indiceSerie) => {
                const valor = ponto.values[s.key] ?? 0;
                const alturaPercentual = Math.max((valor / maximo) * 100, valor > 0 ? 2 : 0);
                const cor = s.color ?? 'var(--pg-color-destaque)';
                const atraso = (indiceGrupo * series.length + indiceSerie) * 20;
                const ehMaior = indiceGrupo * series.length + indiceSerie === indiceMaiorValor;
                const tooltip = `
              <div class="pg-tooltip">
                <strong>${escaparHtml(ponto.label)}</strong>
                <div class="pg-tooltip-linha"><span class="pg-tooltip-cor" style="background:${cor}"></span>${escaparHtml(s.label || 'Valor')}<span class="pg-tooltip-valor">${escaparHtml(this._formatarValor(valor))}</span></div>
              </div>`;
                return `
              <div class="coluna-wrap${ehMaior ? ' coluna-wrap--destaque' : ''}">
                <span class="coluna-valor">${escaparHtml(this._formatarValor(valor))}</span>
                <div class="coluna${ehMaior ? ' coluna--destaque' : ''}" style="height:${alturaPercentual}%;background:linear-gradient(180deg, color-mix(in srgb, ${cor} 88%, white) 0%, ${cor} 55%, color-mix(in srgb, ${cor} 82%, black) 100%);animation-delay:${atraso}ms">${tooltip}</div>
              </div>`;
            })
                .join('');
            return `
          <div class="grupo-barra">
            <div class="colunas">${colunas}</div>
            <span class="rotulo-x" title="${escaparHtml(ponto.label)}">${escaparHtml(ponto.label)}</span>
          </div>`;
        })
            .join('');
        return `${legenda}<div class="barras">${grupos}</div>`;
    }
    /**
     * Linha (SVG puro, viewBox `0 0 100 100` para escalar por CSS sem
     * recalcular pontos em resize) — mesmo modelo de dado de `renderBarras`
     * (`series`+`points`), só a forma visual muda (evolução ao longo do
     * eixo X, não comparação por categoria).
     *
     * Convenção: a 1ª série do array é a "principal" (ganha preenchimento
     * de área e traço mais grosso; as demais são linhas de apoio, mais
     * finas, sem área) — evita duplicar o componente só para dar
     * destaque a uma métrica (ex: Faturamento vs. Lucro/Despesas no
     * Dashboard).
     *
     * O tooltip é por período (uma coluna invisível por ponto do eixo X),
     * não por marcador — passar o mouse em qualquer altura daquela coluna
     * mostra todas as séries daquele período de uma vez, como um gráfico
     * financeiro de verdade.
     */
    renderLinha() {
        if (this._points.length === 0) {
            return `<p class="vazio">Sem dados no período.</p>`;
        }
        const series = this._series.length > 0 ? this._series : [{ key: 'valor', label: '' }];
        const todosValores = this._points.flatMap((ponto) => series.map((s) => ponto.values[s.key] ?? 0));
        // Domínio SEMPRE inclui 0 (`Math.max(0, ...)`/`Math.min(0, ...)`) — é o que ancora a linha de
        // base do eixo, mesmo quando todo valor real for positivo (reproduz o comportamento anterior:
        // `valorMinimo` vira 0, `amplitude` vira `valorMaximoBruto`, igual à escala 0→máximo de antes) ou
        // todo negativo (aí `valorMaximoBruto` vira 0 e a série ocupa a metade de cima a partir do zero).
        const valorMaximoBruto = Math.max(0, ...todosValores);
        const valorMinimo = Math.min(0, ...todosValores);
        const amplitudeReal = valorMaximoBruto - valorMinimo;
        // Respiro visual de 12% acima do maior valor real — o pico nunca toca a borda do card. Só
        // aplicado quando existe de fato um valor positivo real no topo (`valorMaximoBruto > 0`) — com
        // toda a série negativa, `valorMaximoBruto` é só o piso sintético (zero), não um pico de dado
        // real, e o zero precisa continuar exatamente no topo (y=0%) para as barras negativas terem o
        // máximo de espaço vertical (comportamento já validado em `verify-render.mjs`, não alterado aqui).
        const margemTopo = valorMaximoBruto > 0 ? amplitudeReal * 0.12 : 0;
        const valorMaximo = valorMaximoBruto + margemTopo;
        const amplitude = valorMaximo - valorMinimo || 1;
        const n = this._points.length;
        const passoX = n > 1 ? 100 / (n - 1) : 0;
        // Escala linear própria do domínio [valorMinimo, valorMaximo] → [100, 0] (SVG cresce pra baixo).
        // SEM clamp em 0: um valor negativo (ex: mês de prejuízo) precisa desenhar abaixo da linha de
        // zero, não ficar achatado na mesma posição de um valor exatamente zero (regressão corrigida
        // aqui — a versão anterior fazia `Math.max(0, valor)` antes de escalar, o que colapsava
        // qualquer prejuízo para a base do gráfico, indistinguível de lucro zero).
        const y = (valor) => 100 - ((valor - valorMinimo) / amplitude) * 100;
        const yZero = y(0);
        const temValorNegativo = valorMinimo < 0;
        const legenda = series.length > 1
            ? `<div class="legenda">${series
                .map((s) => `<span class="legenda-item"><span class="legenda-cor" style="background:${s.color ?? 'var(--pg-color-destaque)'}"></span>${escaparHtml(s.label)}</span>`)
                .join('')}</div>`
            : '';
        const gradeHorizontal = [25, 50, 75].map((linhaY) => `<line x1="0" y1="${linhaY}" x2="100" y2="${linhaY}" />`).join('');
        // Grid vertical esparso (5 colunas) — só um guia discreto de progressão no eixo X, não 1 linha
        // por ponto (com muitos pontos, como 60 dias de histórico, isso viraria uma grade cheia demais).
        const gradeVertical = [0, 25, 50, 75, 100].map((linhaX) => `<line x1="${linhaX}" y1="0" x2="${linhaX}" y2="100" />`).join('');
        const grade = gradeHorizontal + gradeVertical;
        // Linha de referência do zero — só desenhada quando o domínio de fato cruza zero (há valor
        // negativo real); com todo valor ≥0 o zero já coincide com a base do gráfico (y=100, mesma
        // posição da última linha da grade horizontal), então marcá-la de novo seria redundante.
        const linhaZero = temValorNegativo ? `<line class="linha-zero" x1="0" y1="${yZero}" x2="100" y2="${yZero}" />` : '';
        // Eixo Y monetário — 3 marcas esparsas, mas a do TOPO é sempre o maior valor real
        // (`valorMaximoBruto`), nunca uma fração arbitrária do domínio já com margem (`valorMaximo`) —
        // com frações fixas (0.75/0.5/0.25) a marca mais alta ficava abaixo do pico real da linha
        // (a própria margem de respiro), dando a falsa impressão visual de que a linha "não chegava" ao
        // valor real (bug corrigido aqui). As outras duas dividem [valorMinimo, valorMaximoBruto] ao
        // meio. Posição de cada marca calculada sobre o domínio COM margem (`amplitude`), mesma escala
        // usada por `y()`, então a marca do topo cai visualmente onde a linha de fato passa.
        const eixoY = [valorMaximoBruto, valorMinimo + amplitudeReal / 2, valorMinimo]
            .map((valor) => `<span style="top:${100 - ((valor - valorMinimo) / amplitude) * 100}%">${escaparHtml(this._formatarValor(valor))}</span>`)
            .join('');
        // Coordenadas x/y de cada série em cada ponto do eixo X — calculadas uma vez e reaproveitadas
        // tanto para desenhar as curvas quanto para os marcadores e o tooltip consolidado por período.
        const porIndice = this._points.map((ponto, i) => ({
            x: n > 1 ? i * passoX : 50,
            rotulo: ponto.label,
            itens: series.map((s) => ({
                label: s.label || 'Valor',
                cor: s.color ?? 'var(--pg-color-destaque)',
                valor: ponto.values[s.key] ?? 0,
                y: y(ponto.values[s.key] ?? 0),
            })),
        }));
        const defs = [];
        const marcadoresHtml = [];
        const linhas = series
            .map((s, indiceSerie) => {
            const cor = s.color ?? 'var(--pg-color-destaque)';
            const principal = indiceSerie === 0;
            const coordenadas = porIndice.map((p) => ({ x: p.x, y: p.itens[indiceSerie].y }));
            coordenadas.forEach((c) => {
                marcadoresHtml.push(`<div class="linha-ponto-html${principal ? ' linha-ponto-html--principal' : ''}" style="left:${c.x}%;top:${c.y}%;background:${cor};"></div>`);
            });
            const idGradiente = `pg-chart-area-${indiceSerie}-${Math.round(Math.random() * 1e6)}`;
            defs.push(`<linearGradient id="${idGradiente}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${cor}" stop-opacity="0.18" /><stop offset="18%" stop-color="${cor}" stop-opacity="0.04" /><stop offset="100%" stop-color="${cor}" stop-opacity="0" /></linearGradient>`);
            // Traço fino e uniforme entre as 3 séries (~2px reais) — a hierarquia visual de Faturamento
            // como série principal vem só da área de preenchimento discreta, não da espessura da linha;
            // um traço mais grosso é o que deixava o gráfico com "cara de área preenchida" em vez de
            // painel financeiro técnico. Sem `vector-effect="non-scaling-stroke"` (ver nota abaixo), esse
            // valor na escala local de 100 unidades é multiplicado por 2 (altura fixa de 200px do card /
            // 100 unidades do viewBox) para virar pixels reais — 0,6 aqui sai como ~1,2px renderizado.
            const espessura = 0.6;
            const atraso = indiceSerie * 70;
            if (n === 1) {
                // Só 1 período real: nada de "conectar 2 pontos fictícios". Uma linha de referência
                // pontilhada no valor real (não é tendência, é só uma marca no eixo) — honesto e ainda
                // parece um componente completo, não um ponto perdido sozinho no card.
                const c = coordenadas[0];
                // Fecha a área no zero real (`yZero`), não na base fixa do gráfico (y=100) — com todo
                // valor ≥0 os dois coincidem (comportamento preservado); com o único ponto negativo, a
                // área passa a se estender do zero PARA BAIXO até a curva, não do zero até o fundo do
                // card inteiro.
                const area = principal ? `<path class="linha-area" d="M 0,${c.y} L 100,${c.y} L 100,${yZero} L 0,${yZero} Z" fill="url(#${idGradiente})" stroke="none" />` : '';
                return `
            ${area}
            <line class="linha-referencia" x1="0" y1="${c.y}" x2="100" y2="${c.y}" stroke="${cor}" stroke-width="${espessura}" stroke-dasharray="3 3" style="animation-delay:${atraso}ms" />
          `;
            }
            const caminho = caminhoLinear(coordenadas);
            // Mesma correção do ramo de 1 ponto acima: fecha no zero real (`yZero`), não em y=100 fixo.
            const areaD = principal
                ? `${caminho} L ${coordenadas[coordenadas.length - 1].x},${yZero} L ${coordenadas[0].x},${yZero} Z`
                : '';
            // Sem `vector-effect="non-scaling-stroke"` de propósito: sob o viewBox quadrado esticado de
            // forma bem não-uniforme (card largo/baixo), ele fazia o Chrome desenhar a curva em pedaços
            // com gaps visíveis entre os segmentos da Bézier — confirmado removendo o atributo. Como o
            // traçado é predominantemente horizontal, a largura do traço sem essa correção já fica
            // uniforme na prática (a distorção só apareceria em trechos quase verticais, incomuns aqui).
            return `
          ${principal ? `<path class="linha-area" d="${areaD}" fill="url(#${idGradiente})" stroke="none" style="animation-delay:${atraso}ms" />` : ''}
          <path class="linha-caminho" d="${caminho}" fill="none" stroke="${cor}" stroke-width="${espessura}" pathLength="1" stroke-dasharray="1" style="color:${cor};animation-delay:${atraso}ms" />
        `;
        })
            .join('');
        // Colunas de hover: uma por período, largura = distância até o meio do próximo/anterior ponto —
        // cobre 100% da largura sem sobreposição, então passar o mouse em qualquer altura já funciona
        // (não precisa acertar o marcador exato).
        const colunasHover = porIndice
            .map((p, i) => {
            const esquerda = i === 0 ? 0 : (porIndice[i - 1].x + p.x) / 2;
            const direita = i === n - 1 ? 100 : (p.x + porIndice[i + 1].x) / 2;
            const largura = direita - esquerda;
            const guiaX = largura > 0 ? ((p.x - esquerda) / largura) * 100 : 50;
            const topoY = Math.min(...p.itens.map((it) => it.y));
            const linhasTooltip = p.itens
                .map((it) => `<div class="pg-tooltip-linha"><span class="pg-tooltip-cor" style="background:${it.cor}"></span>${escaparHtml(it.label)}<span class="pg-tooltip-valor">${escaparHtml(this._formatarValor(it.valor))}</span></div>`)
                .join('');
            return `
          <div class="linha-coluna-hover" style="left:${esquerda}%;width:${largura}%;">
            <div class="linha-guia" style="left:${guiaX}%;"></div>
            <div class="pg-tooltip" style="top:${topoY}%;">
              <strong>${escaparHtml(p.rotulo)}</strong>
              ${linhasTooltip}
            </div>
          </div>`;
        })
            .join('');
        const rotulosX = this._points.map((p) => `<span title="${escaparHtml(p.label)}">${escaparHtml(p.label)}</span>`).join('');
        return `
      ${legenda}
      <div class="linha-wrap">
        <div class="linha-eixo-y">${eixoY}</div>
        <div class="linha-plotagem">
          <svg class="linha-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>${defs.join('')}</defs>
            <g class="linha-grade">${grade}</g>
            ${linhaZero}
            ${linhas}
          </svg>
          <div class="linha-marcadores">${marcadoresHtml.join('')}</div>
          <div class="linha-colunas-hover">${colunasHover}</div>
        </div>
      </div>
      <div class="linha-rotulos">${rotulosX}</div>
    `;
    }
    /**
     * Donut — cada `point` é uma fatia (1 valor, via `series[0].key` ou
     * `'valor'` se `series` não for informado); cor via `point.color`,
     * com fallback rotativo pela paleta de destaque/dourado/sucesso/alerta.
     * Construído com `stroke-dasharray`/`stroke-dashoffset` sobre um
     * `<circle>` (técnica padrão de donut em SVG puro, sem lib externa).
     */
    renderDonut() {
        if (this._points.length === 0) {
            return `<p class="vazio">Sem dados no período.</p>`;
        }
        const chave = this._series[0]?.key ?? 'valor';
        const paletaPadrao = [
            'var(--pg-color-destaque)',
            'var(--pg-color-dourado)',
            'var(--pg-color-sucesso)',
            'var(--pg-color-alerta)',
            'var(--pg-color-erro)',
        ];
        const fatias = this._points.map((ponto, i) => ({
            label: ponto.label,
            valor: Math.max(0, ponto.values[chave] ?? 0),
            cor: ponto.color ?? paletaPadrao[i % paletaPadrao.length],
        }));
        const total = fatias.reduce((soma, f) => soma + f.valor, 0);
        if (total === 0) {
            return `<p class="vazio">Sem dados no período.</p>`;
        }
        const raio = 15.9155; // circunferência = 100, facilita converter valor->percentual direto em dasharray
        const circunferencia = 2 * Math.PI * raio;
        const fatiasVisiveis = fatias.filter((f) => f.valor > 0);
        // Pequeno respiro entre fatias (estilo BI premium) — só faz sentido com 2+ fatias reais.
        const gap = fatiasVisiveis.length > 1 ? 1.6 : 0;
        let acumulado = 0;
        const defsDonut = [];
        const arcos = fatiasVisiveis
            .map((f, i) => {
            const percentual = f.valor / total;
            const comprimento = percentual * circunferencia;
            const comprimentoVisual = Math.max(0.6, comprimento - gap);
            const dasharray = `${comprimentoVisual} ${circunferencia - comprimentoVisual}`;
            const dashoffset = -(acumulado * circunferencia + gap / 2);
            acumulado += percentual;
            // Gradiente relativo ao bbox do círculo inteiro (mesmo pra todas as fatias, já que é o
            // mesmo <circle> por baixo) — dá a impressão de uma única fonte de luz diagonal sobre o
            // anel inteiro, em vez de cor lisa por fatia.
            const idGradienteFatia = `pg-chart-donut-${i}-${Math.round(Math.random() * 1e6)}`;
            defsDonut.push(`<linearGradient id="${idGradienteFatia}" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="color-mix(in srgb, ${f.cor} 82%, white)" /><stop offset="100%" stop-color="color-mix(in srgb, ${f.cor} 85%, black)" /></linearGradient>`);
            return `<circle class="donut-fatia" cx="21" cy="21" r="${raio}" fill="none" stroke="url(#${idGradienteFatia})" stroke-width="7.5" stroke-linecap="round" stroke-dasharray="${dasharray}" stroke-dashoffset="${dashoffset}"><title>${escaparHtml(f.label)}: ${escaparHtml(this._formatarValor(f.valor))} (${formatarPercentual(percentual)})</title></circle>`;
        })
            .join('');
        const legenda = fatias
            .map((f) => `
        <span class="donut-legenda-item">
          <span class="legenda-cor" style="background:${f.cor}"></span>
          ${escaparHtml(f.label)}
          <span class="donut-legenda-valores">
            <span class="donut-legenda-valor">${escaparHtml(this._formatarValor(f.valor))}</span>
            <span class="donut-legenda-percentual">${formatarPercentual(f.valor / total)}</span>
          </span>
        </span>`)
            .join('');
        return `
      <div class="donut-wrap">
        <svg class="donut-svg" width="152" height="152" viewBox="0 0 42 42">
          <defs>${defsDonut.join('')}</defs>
          <circle cx="21" cy="21" r="${raio}" fill="none" stroke="var(--pg-color-bg-base, #121417)" stroke-width="7.5" />
          <g transform="rotate(-90 21 21)">${arcos}</g>
          <text x="21" y="19.5" text-anchor="middle" class="donut-centro-valor">${escaparHtml(this._formatarValor(total))}</text>
          <text x="21" y="25" text-anchor="middle" class="donut-centro-rotulo">Total</text>
        </svg>
        <div class="donut-legenda">${legenda}</div>
      </div>
    `;
    }
    renderHeatmap() {
        if (this._cells.length === 0) {
            return `<p class="vazio">Sem envios despachados no período.</p>`;
        }
        const maximo = Math.max(1, ...this._cells.map((c) => c.quantidade));
        const porData = new Map(this._cells.map((c) => [c.data, c.quantidade]));
        // Com `dias` informado, desenha o calendário real completo do período consultado (dias sem envio
        // também são dado real — "nenhum envio nesse dia" — em vez de só o trecho entre a 1ª/última célula com valor,
        // que fica parecendo um punhado perdido de quadrados soltos quando os envios são esparsos.
        let inicio;
        let fim;
        if (this._dias !== null) {
            fim = new Date();
            fim.setUTCHours(0, 0, 0, 0);
            inicio = new Date(fim);
            inicio.setUTCDate(inicio.getUTCDate() - (this._dias - 1));
        }
        else {
            const datas = this._cells.map((c) => new Date(`${c.data}T00:00:00Z`).getTime());
            inicio = new Date(Math.min(...datas));
            fim = new Date(Math.max(...datas));
        }
        // Recua até domingo, para alinhar semanas em colunas (mesmo padrão de heatmap de calendário do GitHub).
        inicio.setUTCDate(inicio.getUTCDate() - inicio.getUTCDay());
        const semanas = [];
        let semanaAtual = [];
        const cursor = new Date(inicio);
        while (cursor.getTime() <= fim.getTime()) {
            const chave = cursor.toISOString().slice(0, 10);
            const quantidade = porData.get(chave) ?? 0;
            const intensidade = quantidade === 0 ? 0 : Math.min(4, Math.ceil((quantidade / maximo) * 4));
            const opacidade = [0, 0.25, 0.5, 0.75, 1][intensidade];
            const cor = intensidade === 0 ? 'var(--pg-color-bg-elevado, #1a1d21)' : `color-mix(in srgb, var(--pg-color-destaque, #2f8cff) ${opacidade * 100}%, var(--pg-color-bg-elevado, #1a1d21))`;
            semanaAtual.push(`<div class="heatmap-dia" style="background:${cor}" title="${chave}: ${quantidade} envio(s)"></div>`);
            if (cursor.getUTCDay() === 6) {
                semanas.push(`<div class="heatmap-semana">${semanaAtual.join('')}</div>`);
                semanaAtual = [];
            }
            cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
        if (semanaAtual.length > 0)
            semanas.push(`<div class="heatmap-semana">${semanaAtual.join('')}</div>`);
        const rotulosDiaSemana = ['', 'Seg', '', 'Qua', '', 'Sex', ''];
        return `
      <div class="heatmap-corpo">
        <div class="heatmap-dias-semana">${rotulosDiaSemana.map((r) => `<span>${r}</span>`).join('')}</div>
        <div class="heatmap-semanas">${semanas.join('')}</div>
      </div>
      <div class="heatmap-legenda">
        <span>Menos</span>
        <div class="heatmap-dia" style="background:var(--pg-color-bg-elevado, #1a1d21)"></div>
        <div class="heatmap-dia" style="background:color-mix(in srgb, var(--pg-color-destaque, #2f8cff) 50%, var(--pg-color-bg-elevado, #1a1d21))"></div>
        <div class="heatmap-dia" style="background:var(--pg-color-destaque, #2f8cff)"></div>
        <span>Mais</span>
      </div>
    `;
    }
}
/**
 * Converte uma polilinha em caminho SVG. Curva suave (quadráticas
 * passando pelo PONTO MÉDIO entre vizinhos, com o dado real como ponto
 * de controle — mesma técnica, já validada, de `svgSparkline` acima)
 * pedida explicitamente na sessão de refinamento visual do Dashboard
 * CEO ("dashboard SaaS premium"). Antes disso o traçado era só
 * segmentos retos (`L`) — uma spline cheia (Catmull-Rom/cúbica
 * monótona) já tinha sido tentada e descartada por "ler como onda"; a
 * técnica aqui é mais leve de propósito (o valor real de cada ponto
 * nunca é deslocado — só a transição ATÉ ele é suavizada), então não
 * reintroduz esse problema. `stroke-linejoin: round` (CSS) continua
 * arredondando as poucas quinas retas que sobram nas pontas.
 */
function caminhoLinear(pontos) {
    if (pontos.length < 3) {
        return pontos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
    }
    let caminho = `M ${pontos[0].x},${pontos[0].y}`;
    caminho += ` L ${(pontos[0].x + pontos[1].x) / 2},${(pontos[0].y + pontos[1].y) / 2}`;
    for (let i = 1; i < pontos.length - 1; i++) {
        const atual = pontos[i];
        const proximo = pontos[i + 1];
        caminho += ` Q ${atual.x},${atual.y} ${(atual.x + proximo.x) / 2},${(atual.y + proximo.y) / 2}`;
    }
    const ultimo = pontos[pontos.length - 1];
    caminho += ` L ${ultimo.x},${ultimo.y}`;
    return caminho;
}
function formatarPercentual(fracao) {
    return `${(fracao * 100).toFixed(fracao >= 0.1 ? 0 : 1).replace('.', ',')}%`;
}
function escaparHtml(valor) {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
}
customElements.define('pg-chart', PgChart);
//# sourceMappingURL=pg-chart.js.map